import React, { useState, useMemo, useRef, useEffect, type FC } from "react";
import type { EditorAttachment } from "../Attachments";
import { Modal, Input, Checkbox, Toggle, Tooltip } from "../../../DesignSystem";
import Button from "../../../DesignSystem/Button";
import StyledDatePicker from "../../../Common/StyledDatePicker";
import FileIcon from "../../../Common/FileIcon";
import { AlertTriangle, ArrowLeft, ChevronDown, Info, Shield, ShieldCheck, Trash2 } from "lucide-react";
import { CountryCodes } from "../../../../util/CountryCodes";
import { mimeTypeToString, bytesToPretty } from "../../../../util/formatting";
import { _2D_GEOMETRY_FILE_TYPES, _3D_GEOMETRY_FILE_TYPES } from "../../../../util/file";
import { DropdownMenu } from "radix-ui";
import documentStack from "../../../../assets/images/document_stack.svg";
import { useFeatureAccess, FEATURE_KEYS } from "../../../../util/useFeatureAccess";

export interface AttachFilesResult {
    regularFiles: File[];
    secureFiles: File[];
    secureConfig: {
        geoRestrictedCountries: string[];
        maxDownloads: number | undefined;
        expirationDate: number | null;
        processMetadata: boolean;
    };
}

interface AttachFileModalProps {
    open: boolean;
    onClose: () => void;
    onAttach: (result: AttachFilesResult) => void;
    initialStep?: 1 | 2 | 3;
    initialConfig?: EditorAttachment["secureConfig"];
}

const COUNTRIES = Object.entries(CountryCodes).map(([code, name]) => ({ code, name }));
const SECURITY_WARNING_DISMISSED_KEY = "upload_security_warning_dismissed";

type FileEntry = { file: File; secure: boolean; id: string };
type Step = 1 | 2 | 3;

const SECURE_INFO_CONTENT = (
    <div className="max-w-xs space-y-2 text-sm leading-relaxed">
        <p>Your files are never attached directly to the email.</p>
        <div>
            <p className="mb-1">To access the file, they must:</p>
            <ol className="list-decimal space-y-0.5 pl-4">
                <li>Enter their email address</li>
                <li>Receive a one-time code via email</li>
                <li>Enter the code to download the file</li>
            </ol>
        </div>
        <p>Every access attempt is logged automatically.</p>
    </div>
);

export const AttachFileModal: FC<AttachFileModalProps> = ({ open, onClose, onAttach, initialStep, initialConfig }) => {
    const { isFeatureEnabled } = useFeatureAccess();
    const secureFileShareEnabled = isFeatureEnabled(FEATURE_KEYS.SECURE_FILE_SHARE);
    const [step, setStep] = useState<Step>(1);

    useEffect(() => {
        if (open) {
            setStep(initialStep ?? 1);
            if (initialConfig) {
                setMaxDownloads(initialConfig.maxDownloads);
                setExpirationDate(initialConfig.expirationDate ?? null);
                setSelectedCountries(initialConfig.geoRestrictedCountries ?? []);
                setProcessMetadata(initialConfig.processMetadata ?? false);
            }
        }
    }, [open, initialStep, initialConfig]);
    const [fileEntries, setFileEntries] = useState<FileEntry[]>([]);
    const [isDragging, setIsDragging] = useState(false);

    // Security options (step 3)
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [countrySearch, setCountrySearch] = useState("");
    const [maxDownloads, setMaxDownloads] = useState<number | undefined>(undefined);
    const [expirationDate, setExpirationDate] = useState<number | null>(null);
    const [processMetadata, setProcessMetadata] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [showWarning, setShowWarning] = useState(false);
    const [warningDontShowAgain, setWarningDontShowAgain] = useState(false);
    const [warningInitialSecureIds, setWarningInitialSecureIds] = useState<Set<string>>(new Set());

    const anySecure = fileEntries.some(e => e.secure);
    const allSecure = fileEntries.length > 0 && fileEntries.every(e => e.secure);
    const regularEntries = fileEntries.filter(e => !e.secure);


    const filteredCountries = useMemo(() => {
        if (!countrySearch) return COUNTRIES;
        const lower = countrySearch.toLowerCase();
        return COUNTRIES.filter(c => c.name.toLowerCase().includes(lower) || c.code.toLowerCase().includes(lower));
    }, [countrySearch]);

    const countryTriggerLabel =
        selectedCountries.length === 0
            ? "Any Country"
            : selectedCountries.length === 1
                ? (CountryCodes[selectedCountries[0] as keyof typeof CountryCodes] ?? selectedCountries[0])
                : `${selectedCountries.length} Countries`;

    const handleFilesAdded = (newFiles: File[]) => {
        setFileEntries(prev => {
            const shouldBeSecure = prev.length > 0 && prev.every(e => e.secure);
            return [
                ...prev,
                ...newFiles.map(file => ({
                    file,
                    secure: shouldBeSecure,
                    id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                })),
            ];
        });
        setStep(prev => (prev === 1 ? 2 : prev));
    };

    const removeFile = (id: string) => {
        setFileEntries(prev => {
            const next = prev.filter(e => e.id !== id);
            if (next.length === 0) setStep(1);
            return next;
        });
    };

    const toggleFileSecure = (id: string) => {
        setFileEntries(prev => prev.map(e => (e.id === id ? { ...e, secure: !e.secure } : e)));
    };

    const handleCountryToggle = (code: string) => {
        setSelectedCountries(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]));
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = Array.from(e.dataTransfer.files);
        if (dropped.length > 0) handleFilesAdded(dropped);
    };

    const proceedFromStep2 = () => {
        if (anySecure) {
            setStep(3);
        } else {
            onAttach({
                regularFiles: fileEntries.map(e => e.file),
                secureFiles: [],
                secureConfig: { geoRestrictedCountries: [], maxDownloads: undefined, expirationDate: null, processMetadata: false },
            });
            handleClose();
        }
    };

    const handlePrimaryClick = () => {
        if (step === 2) {
            const warningDismissed = localStorage.getItem(SECURITY_WARNING_DISMISSED_KEY) === "true";
            if (secureFileShareEnabled && regularEntries.length > 0 && !warningDismissed) {
                setWarningInitialSecureIds(new Set(fileEntries.filter(e => e.secure).map(e => e.id)));
                setShowWarning(true);
                return;
            }
            proceedFromStep2();
        } else if (step === 3) {
            onAttach({
                regularFiles: fileEntries.filter(e => !e.secure).map(e => e.file),
                secureFiles: fileEntries.filter(e => e.secure).map(e => e.file),
                secureConfig: { geoRestrictedCountries: selectedCountries, maxDownloads, expirationDate, processMetadata },
            });
            handleClose();
        }
    };

    const handleWarningConfirm = () => {
        if (warningDontShowAgain) {
            localStorage.setItem(SECURITY_WARNING_DISMISSED_KEY, "true");
        }
        setShowWarning(false);
        proceedFromStep2();
    };

    const handleClose = () => {
        setStep(1);
        setFileEntries([]);
        setIsDragging(false);
        setShowWarning(false);
        setWarningDontShowAgain(false);
        setWarningInitialSecureIds(new Set());
        setSelectedCountries([]);
        setCountrySearch("");
        setMaxDownloads(undefined);
        setExpirationDate(null);
        setProcessMetadata(false);
        onClose();
    };

    // Modal title changes per step
    const modalTitle =
        step === 3 ? (
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex items-center justify-center rounded-md p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600 focus:outline-none"
                    aria-label="Back"
                >
                    <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="mx-1 h-3.5 w-px bg-stone-200" />
                <Shield className="h-4 w-4 text-stone-500" />
                <span>
                    Secure File Share
                </span>
            </div>
        ) : step === 2 && showWarning ? (
            <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span>Some files will be sent as standard attachments</span>
            </div>
        ) : (
            "Upload Attachments"
        );

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={modalTitle}
            titleClassName="mb-0"
            showCloseButton={true}
            className="sm:max-w-[700px] md:max-w-[700px] lg:max-w-[700px] min-h-[375px] max-h-[600px]"
            childrenClassName="flex flex-col flex-1"
            overlayClassName="z-[60]"
        >
            {/* Single hidden file input shared across steps */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={`application/pdf,image/png,image/jpeg,image/webp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,${_3D_GEOMETRY_FILE_TYPES.join(",")},${_2D_GEOMETRY_FILE_TYPES.join(",")}`}
                className="hidden"
                onChange={e => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) handleFilesAdded(files);
                    e.target.value = "";
                }}
            />

            {/* Divider below title */}
            <div className="-mx-5 my-3 border-t border-stone-200" />

            {/* ── Step 1: Empty drop zone ── */}
            {step === 1 && (
                <div
                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex flex-1 cursor-pointer flex-col items-center justify-center gap-6 rounded-lg py-12 transition-colors ${isDragging ? "bg-bronze-100" : "bg-stone-50 hover:bg-stone-100"}`}
                >
                    <img src={documentStack} alt="" className="h-24 w-auto" />
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <span className="font-medium text-stone-950">Drag and Drop</span>
                        <span className="text-stone-500">or</span>
                        <Button
                            color="bronze"
                            text="Browse Files"
                            onClick={() => fileInputRef.current?.click()}
                        />
                    </div>
                </div>
            )}

            {/* ── Step 2: Security warning confirmation ── */}
            {step === 2 && showWarning && (
                <div className="flex flex-1 flex-col gap-4">
                    <div className="flex flex-col gap-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <p className="text-md text-stone-700">
                            The files below will be attached directly to the email. Anyone who receives the email can open or download them with no access controls, no download limits, and no expiration.
                        </p>
                        <p className="text-md text-stone-700">
                            If any of these files are sensitive (engineering drawings, proprietary specs, or confidential documents), we recommend sending them as secure links instead.
                        </p>
                        <div className="flex flex-col gap-1.5">
                            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Sending as standard attachments</p>
                            <div className="flex max-h-[160px] flex-col overflow-y-auto rounded-lg border border-stone-200 bg-white">
                                {fileEntries.filter(e => !warningInitialSecureIds.has(e.id)).map((entry, i) => (
                                    <div key={entry.id} className="flex items-center gap-2 px-3 py-2 text-sm" style={i > 0 ? { borderTop: "1px solid", borderImage: "linear-gradient(to right, transparent 12px, #e7e5e4 12px, #e7e5e4 calc(100% - 12px), transparent calc(100% - 12px)) 1" } : undefined}>
                                        <FileIcon mimeType={entry.file.type} className="h-4 w-4 shrink-0" />
                                        <span className={`min-w-0 flex-1 truncate font-medium ${entry.secure ? "text-stone-400 line-through" : "text-stone-950"}`}>
                                            {entry.file.name}
                                        </span>
                                        <Tooltip text={entry.secure ? "Sending securely | Click to remove." : "Send as secure link."} showDelay={150} verticalOffset={4}>
                                            <button
                                                type="button"
                                                onClick={() => toggleFileSecure(entry.id)}
                                                className={`flex shrink-0 items-center justify-center rounded-lg p-1.5 transition-colors focus:outline-none ${entry.secure ? "bg-[#3730a3] text-white" : "text-stone-400 hover:bg-stone-100 hover:text-indigo-600"}`}
                                            >
                                                {entry.secure ? <ShieldCheck className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                                            </button>
                                        </Tooltip>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="-mx-5 mt-auto flex items-center justify-between border-t border-stone-100 px-5 pt-3">
                        <div className="flex items-center gap-2">
                            <Button color="bronze" text={regularEntries.length === 0 ? "Continue" : "Send as Standard Attachments"} onClick={handleWarningConfirm} />
                            <Button color="white" text="Review Files" onClick={() => setShowWarning(false)} />
                        </div>
                        <label className="flex cursor-pointer items-center gap-1 text-sm text-stone-500">
                            <Checkbox checked={warningDontShowAgain} onClick={() => setWarningDontShowAgain(v => !v)} />
                            Don't show again
                        </label>
                    </div>
                </div>
            )}

            {/* ── Step 2: Files added ── */}
            {step === 2 && !showWarning && (
                <div className="flex flex-1 flex-col gap-2">
                    {/* Upload additional documents strip */}
                    <div
                        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                        className={`flex items-center justify-between rounded-lg p-4 transition-colors ${isDragging ? "bg-bronze-100" : "bg-stone-50"}`}
                    >
                        <span className="font-medium text-stone-950">Upload Additional Documents</span>
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-stone-950">Drag and Drop</span>
                            <span className="text-stone-500">or</span>
                            <Button
                                color="white"
                                text="Browse Files"
                                onClick={() => fileInputRef.current?.click()}
                            />
                        </div>
                    </div>

                    {/* File list */}
                    <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
                        {fileEntries.map(entry => (
                            <div
                                key={entry.id}
                                className={`flex items-center gap-3 rounded-xl p-2 ${entry.secure ? "bg-gradient-to-r from-indigo-50 to-white" : ""}`}
                            >
                                {/* File type icon */}
                                <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-lg bg-stone-50 p-[5px]">
                                    <FileIcon mimeType={entry.file.type} className="h-4 w-4" />
                                </div>

                                {/* File info */}
                                <div className="flex min-w-0 flex-1 items-center gap-2 text-base">
                                    <span className="truncate font-medium text-stone-950">{entry.file.name}</span>
                                    <span className="shrink-0 text-stone-500">{mimeTypeToString(entry.file.type)}</span>
                                    <span className="shrink-0 text-stone-400">•</span>
                                    <span className="shrink-0 text-stone-500">{bytesToPretty(entry.file.size)}</span>
                                </div>

                                {/* Actions */}
                                <div className="flex shrink-0 items-center gap-2 pr-3">
                                    {secureFileShareEnabled && (
                                        <Tooltip
                                            text={entry.secure ? "Sending securely | Click to remove." : "Send file as secure link."}
                                            showDelay={150}
                                            verticalOffset={4}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => toggleFileSecure(entry.id)}
                                                className={`flex items-center justify-center rounded-lg p-2 transition-colors focus:outline-none ${entry.secure
                                                    ? "bg-[#3730a3] text-white"
                                                    : "text-stone-400 hover:bg-stone-100 hover:text-stone-600"
                                                    }`}
                                            >
                                                {entry.secure ? (
                                                    <ShieldCheck className="h-4 w-4" />
                                                ) : (
                                                    <Shield className="h-4 w-4" />
                                                )}
                                            </button>
                                        </Tooltip>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => removeFile(entry.id)}
                                        className="flex items-center justify-center rounded-lg p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-600 focus:outline-none"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="-mx-5 mt-auto flex items-center justify-between border-t border-stone-100 px-5 pt-3">
                        <div className="flex items-center gap-2">
                            <Button
                                color="bronze"
                                text={anySecure ? "Continue" : "Attach Files"}
                                onClick={handlePrimaryClick}
                            />
                            <Button color="white" text="Cancel" onClick={handleClose} />
                        </div>
                        {secureFileShareEnabled && (
                            <div className="flex items-center gap-2">
                                <Toggle
                                    checked={allSecure}
                                    onChange={checked =>
                                        setFileEntries(prev => prev.map(e => ({ ...e, secure: checked })))
                                    }
                                />
                                <div className="flex items-center gap-1.5 cursor-default">
                                    <span className="text-base text-stone-600">Send all files as secure links.</span>
                                    <Tooltip content={SECURE_INFO_CONTENT} showDelay={200} verticalOffset={4}>
                                        <Info className="h-4 w-4 text-stone-400" />
                                    </Tooltip>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Step 3: Security options ── */}
            {step === 3 && (
                <div className="flex flex-col gap-4">
                    <div className="flex gap-5">
                        {/* Left: form */}
                        <div className="flex flex-1 flex-col gap-4 p-2 max-h-[420px] overflow-y-auto">
                            <p className="text-stone-500">Set access controls for your files.</p>

                            {/* Max Downloads */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-stone-950">Max Downloads per Recipient</label>
                                <Input
                                    type="number"
                                    placeholder="Unlimited"
                                    value={maxDownloads ?? ""}
                                    onChange={e => setMaxDownloads(e.target.value ? parseInt(e.target.value) : undefined)}
                                    className="w-full"
                                />
                            </div>

                            {/* Expiration Date */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-stone-950">Expiration Date</label>
                                <StyledDatePicker
                                    placeholder="No expiration"
                                    value={expirationDate ? new Date(expirationDate) : null}
                                    onChange={date => setExpirationDate(date ? date.getTime() : null)}
                                    triggerClassName="h-[34px] w-full rounded-lg border border-stone-200 bg-white shadow-sm"
                                />
                            </div>

                            {/* Geo Restriction */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-stone-950">Geo Restriction (Allowed Countries)</label>

                                <DropdownMenu.Root>
                                    <DropdownMenu.Trigger asChild>
                                        <button
                                            type="button"
                                            className="flex w-full items-center justify-between rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-sm shadow-sm hover:bg-stone-50 focus:outline-none"
                                        >
                                            <span className={selectedCountries.length === 0 ? "text-stone-500" : "text-stone-950"}>
                                                {countryTriggerLabel}
                                            </span>
                                            <ChevronDown className="h-4 w-4 shrink-0 text-stone-400" />
                                        </button>
                                    </DropdownMenu.Trigger>

                                    <DropdownMenu.Portal>
                                        <DropdownMenu.Content
                                            align="start"
                                            sideOffset={4}
                                            // z-[70] keeps this above the modal overlay (z-[60] on line 234); the default z-50 renders behind it since both are sibling portals at the document root
                                            className="z-[70] w-[var(--radix-dropdown-menu-trigger-width)] rounded-xl border border-stone-200 bg-white p-2 shadow-lg focus:outline-none"
                                        >
                                            <div className="mb-2 px-1">
                                                <Input
                                                    value={countrySearch}
                                                    onChange={e => setCountrySearch(e.target.value)}
                                                    placeholder="Search countries..."
                                                    includeSearchIcon
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {!countrySearch && (
                                                    <DropdownMenu.Item
                                                        className="cursor-pointer focus:outline-none"
                                                        onSelect={() => setSelectedCountries([])}
                                                    >
                                                        <div className="flex items-center gap-2 rounded px-2 py-1 hover:bg-stone-100">
                                                            <Checkbox checked={selectedCountries.length === 0} />
                                                            <span className="text-sm font-medium text-stone-950">Any Country</span>
                                                        </div>
                                                    </DropdownMenu.Item>
                                                )}
                                                {filteredCountries.map(country => (
                                                    <DropdownMenu.Item
                                                        key={country.code}
                                                        className="cursor-pointer focus:outline-none"
                                                        onSelect={e => {
                                                            e.preventDefault();
                                                            handleCountryToggle(country.code);
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2 rounded px-2 py-1 hover:bg-stone-100">
                                                            <Checkbox checked={selectedCountries.includes(country.code)} />
                                                            <span className="text-sm text-stone-800">{country.name}</span>
                                                        </div>
                                                    </DropdownMenu.Item>
                                                ))}
                                                {filteredCountries.length === 0 && (
                                                    <div className="py-2 text-center text-sm text-stone-400">No countries found</div>
                                                )}
                                            </div>
                                        </DropdownMenu.Content>
                                    </DropdownMenu.Portal>
                                </DropdownMenu.Root>

                                {/* Selected country chips */}
                                {selectedCountries.length > 0 && (
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        {selectedCountries.map(code => (
                                            <div
                                                key={code}
                                                className="flex items-center gap-1 rounded bg-stone-100 px-2 py-1 text-sm font-medium text-stone-950 select-none"
                                            >
                                                <span>{CountryCodes[code as keyof typeof CountryCodes] ?? code}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCountryToggle(code)}
                                                    className="ml-0.5 text-stone-500 hover:text-stone-700 focus:outline-none"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Metadata Protection */}
                            <div className="flex items-start gap-2">
                                <Checkbox checked={processMetadata} onClick={() => setProcessMetadata(v => !v)} />
                                <div>
                                    <label
                                        className="block cursor-pointer text-sm font-medium text-stone-950"
                                        onClick={() => setProcessMetadata(v => !v)}
                                    >
                                        Enable Metadata Protection (ITAR)
                                    </label>
                                    <p className="text-sm text-stone-500">
                                        Prevents purchaser from extracting any data from the file. Use this for ITAR protected documents.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Right: info card */}
                        <div className="flex w-[280px] shrink-0 flex-col justify-between rounded-lg bg-stone-50 p-4">
                            <div className="flex flex-col gap-1">
                                <p className="font-medium text-stone-950">How does Secure File Share work?</p>
                                <div className="space-y-2 text-sm text-stone-500">
                                    <p>Your files are never attached directly to the email.</p>
                                    <p>To access the file, they must:</p>
                                    <ol className="list-decimal space-y-0.5 pl-4">
                                        <li>Enter their email address</li>
                                        <li>Receive a one-time code via email</li>
                                        <li>Enter the code to download the file</li>
                                    </ol>
                                    <p>Every access attempt is logged automatically.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="-mx-5 flex items-center gap-2 border-t border-stone-100 px-5 pt-3">
                        <Button color="bronze" text={fileEntries.length === 0 ? "Update Settings" : "Attach Files"} onClick={handlePrimaryClick} />
                        <Button color="white" text="Cancel" onClick={handleClose} />
                    </div>
                </div>
            )}
        </Modal>
    );
};
